/*
 * Times and places as Field Mode shows them, and the styles its screens
 * share. The camera has its own (it is always dark); everything else follows
 * the theme.
 */
import { localIsoWithOffset } from '../../utils/specimenCode';

/** A stored moment in milliseconds. The server writes a timestamp the way
 *  Python prints one ("2026-10-06 06:12:00+00:00"), the device in ISO form. */
export function momentMs(value) {
  if (!value) return NaN;
  return Date.parse(String(value).replace(' ', 'T'));
}

const pad = n => String(n).padStart(2, '0');
const dayOf = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Whether a moment fell on this device's today. */
export function isToday(value, now = new Date()) {
  const ms = momentMs(value);
  return Number.isFinite(ms) && dayOf(new Date(ms)) === dayOf(now);
}

/** "09:12" for today, "2026-10-04 09:12" otherwise, on this device's clock. */
export function formatMoment(value, now = new Date()) {
  const ms = momentMs(value);
  if (!Number.isFinite(ms)) return '';
  const local = localIsoWithOffset(new Date(ms));
  const time = local.slice(11, 16);
  return isToday(value, now) ? time : `${local.slice(0, 10)} ${time}`;
}

export function coordText(lat, lng) {
  // Number(null) is 0: a missing coordinate is not the Gulf of Guinea.
  if (lat === null || lng === null || lat === '' || lng === '') return '';
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
  return `${a.toFixed(5)}, ${b.toFixed(5)}`;
}

// Focus that shows on any background, keyboard only (:focus-visible).
export const FOCUS_CSS = '.fm-screen button:focus-visible,.fm-screen select:focus-visible,'
  + '.fm-screen input:focus-visible,.fm-screen textarea:focus-visible,.fm-screen a:focus-visible'
  + '{outline:3px solid var(--c-info);outline-offset:2px}';

const SECTION = {
  display: 'flex', flexDirection: 'column', gap: 10, padding: 14, margin: 0, minWidth: 0,
  background: 'var(--c-surface)', border: '1px solid var(--c-border-2)', borderRadius: 12,
};

export const pageStyles = {
  page: {
    minHeight: '100vh', background: 'var(--c-bg-subtle-2)', display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, zIndex: 5,
    padding: '8px 12px', paddingTop: 'calc(8px + env(safe-area-inset-top))',
    background: 'var(--c-primary-2)', color: 'var(--c-on-accent)',
  },
  headerTitle: { flex: 1, margin: 0, fontWeight: 700, fontSize: 18, outline: 'none' },
  headerBtn: {
    minWidth: 44, minHeight: 44, padding: '0 12px', border: 'none', borderRadius: 10, background: 'none',
    color: 'var(--c-on-accent)', font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  main: {
    flex: 1, padding: 16, maxWidth: 720, width: '100%', margin: '0 auto', boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', gap: 14,
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  },
  section: SECTION,
  h2: { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--c-text-muted-3-fg)' },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-2-fg)' },
  input: {
    fontSize: 16, padding: '11px 12px', border: '1px solid var(--c-border-strong-2)', borderRadius: 8,
    background: 'var(--c-surface)', font: 'inherit', minHeight: 44, boxSizing: 'border-box', width: '100%',
  },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  check: {
    display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, fontSize: 15,
    color: 'var(--c-text-2-fg)', cursor: 'pointer',
  },
  checkbox: { width: 22, height: 22, margin: 0, flexShrink: 0 },
  hits: {
    listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--c-border-strong-2)',
    borderRadius: 8, overflow: 'hidden', background: 'var(--c-surface)',
  },
  hitBtn: {
    display: 'block', width: '100%', textAlign: 'start', padding: '12px 14px', minHeight: 44,
    border: 'none', borderBottom: '1px solid var(--c-bg-subtle-3)', background: 'var(--c-surface)',
    font: 'inherit', fontSize: 15, cursor: 'pointer',
  },
  primary: {
    minHeight: 52, border: 'none', borderRadius: 10, padding: '0 20px', background: 'var(--c-primary-2)',
    color: 'var(--c-on-accent)', font: 'inherit', fontSize: 17, fontWeight: 600, cursor: 'pointer',
  },
  secondary: {
    minHeight: 48, padding: '0 18px', border: '1px solid var(--c-border-strong-2)', borderRadius: 10,
    background: 'var(--c-surface)', color: 'var(--c-text-2-fg)', font: 'inherit', fontSize: 16, cursor: 'pointer',
  },
  linkBtn: {
    minHeight: 44, border: 'none', background: 'none', color: 'var(--c-info-2-fg)', font: 'inherit',
    fontSize: 15, cursor: 'pointer', textDecoration: 'underline', padding: '0 4px',
  },
  hint: { margin: 0, fontSize: 13, color: 'var(--c-text-muted-2-fg)', lineHeight: 1.45 },
  ok: { margin: 0, fontSize: 13, color: 'var(--c-primary-2-fg)', fontWeight: 600 },
  warn: {
    margin: 0, fontSize: 14, color: 'var(--c-danger-4-fg)', background: 'var(--c-danger-soft-2)',
    padding: '10px 12px', borderRadius: 8, lineHeight: 1.45,
  },
  note: {
    margin: 0, fontSize: 14, lineHeight: 1.45, color: 'var(--c-text-body-2-fg)', padding: '10px 12px',
    borderRadius: 8, background: 'var(--c-info-soft-4)', border: '1px solid var(--c-info-soft-5)',
  },
  footer: {
    position: 'sticky', bottom: 0, display: 'flex', flexDirection: 'column', gap: 8,
    margin: '0 -16px', padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
    background: 'var(--c-surface)', borderTop: '1px solid var(--c-border-2)',
  },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', overflowWrap: 'anywhere' },
};

/**
 * Why the server refused something, in the collector's language: by the
 * refusal's code where it has one, else by its status. Never the server's own
 * English sentence, which goes, if anywhere, in a detail of its own (see
 * ServerDetail).
 */
export function refusalReason(t, { status, code } = {}) {
  const byCode = code ? t(`serverCodes.${code}`, { defaultValue: '' }) : '';
  if (byCode) return byCode;
  const byStatus = status ? t(`serverStatus.${status}`, { defaultValue: '' }) : '';
  return byStatus || t('serverStatus.other');
}

/** What a batch waiting for its collector is waiting for, in words. */
export function attentionText(t, attention) {
  const reason = attention?.reason;
  if (reason === 'photos') return t('attention.photos', { count: attention.count || 1 });
  if (reason === 'audio') return t('attention.audio', { count: attention.count || 1 });
  if (reason === 'thermal') return t('attention.thermal', { count: attention.count || 1 });
  if (reason === 'label') return t('attention.label', { code: attention.label || '' });
  if (reason === 'refused' || reason === 'submit') return t(`attention.${reason}`, { why: refusalReason(t, attention) });
  if (reason === 'gone') return t(attention.deleted ? 'attention.goneDeleted' : 'attention.goneMissing');
  return t(`attention.${reason}`);
}
